/**
 * Compute before/after impact metrics for a proposal against the current portfolio.
 * Returns MetricDelta[] compatible with the ImpactMetrics component.
 *
 * Note: "Portfolio Value" has a genuine before/after delta.
 * Turnover and position counts are proposal-only metrics (no meaningful before-state)
 * so labels use "Proposed" prefix to avoid implying a real before baseline.
 */

import type { Proposal, PortfolioSnapshot } from '../../../../stores/proposalStore';

interface MetricDelta {
  label: string;
  before: number;
  after: number;
  unit: string;
  invertColor?: boolean;
}

export function computeProposalImpact(
  proposal: Proposal,
  snapshot: PortfolioSnapshot,
): MetricDelta[] {
  const sellTotal = proposal.trades
    .filter((t) => t.action === 'sell')
    .reduce((sum, t) => sum + t.estimatedValue, 0);
  const buyTotal = proposal.trades
    .filter((t) => t.action === 'buy')
    .reduce((sum, t) => sum + t.estimatedValue, 0);
  const tradeVolume = sellTotal + buyTotal;

  const metrics: MetricDelta[] = [];

  // Portfolio value — genuine before/after delta
  if (snapshot.totalValue > 0) {
    metrics.push({
      label: 'Portfolio Value',
      before: Math.round(snapshot.totalValue / 1000),
      after: Math.round((snapshot.totalValue - sellTotal + buyTotal) / 1000),
      unit: 'K',
    });
  }

  // Trade volume as % of portfolio — proposal-only metric
  const turnoverPct =
    snapshot.totalValue > 0 ? (tradeVolume / snapshot.totalValue) * 100 : 0;
  metrics.push({
    label: 'Proposed Turnover',
    before: 0,
    after: Math.round(turnoverPct * 10) / 10,
    unit: '%',
    invertColor: true,
  });

  // Positions being sold — proposal-only metric
  metrics.push({
    label: 'Proposed Sells',
    before: 0,
    after: proposal.trades.filter((t) => t.action === 'sell').length,
    unit: '',
    invertColor: true,
  });

  // Positions being bought — proposal-only metric
  metrics.push({
    label: 'Proposed Buys',
    before: 0,
    after: proposal.trades.filter((t) => t.action === 'buy').length,
    unit: '',
  });

  return metrics;
}
